'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["relAttr", "onLinkTypeChange", "altText", "loadingValue", "onUpdate", "onSave", "onClose", "imageFileInfo", "onReplace", "showRefAndTargetControls"];
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { View } from 'foundations-components/components/View';
import { Fieldset } from 'foundations-components/transitional/components/Fieldset';
import { FileAttachment } from 'foundations-components/transitional/components/FileAttachment';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { HR } from 'foundations-components/transitional/components/HR';
import { Image } from 'foundations-components/transitional/components/Image';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { LinkTypes } from '../../constants/link';
import { KeyTypes, PopoverType } from '../../constants/popover';
import { LazyLoadingTypes } from '../../constants/shared';
import { useLinkValidation } from '../../hooks/useLinkValidation';
import { isWhitespace } from '../../utils/url';
import ExplicitSaveControls from '../ExplicitSaveControls';
import FormLabelWithHelpLink from '../FormLabelWithHelpLink';
import LazyLoadingInput from '../LazyLoadingInput';
import MorphingPopoverBody from '../MorphingPopoverBody';
import LinkAdvancedPopoverControls from '../links/LinkAdvancedPopoverControls';
import LinkPopoverControlsV2 from '../links/LinkPopoverControlsV2';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const i18nNameSpace = `RichTextLib`;
const i18nKey = suffix => `${i18nNameSpace}.image.${suffix}`;
const linkI18nKey = suffix => `${i18nNameSpace}.links.advanced.${suffix}`;
const getRenderImageAdvancedControls = ({
  relAttr,
  onRelAttrChange,
  linkType,
  loadingValue,
  onLoadingValueChange,
  isSubmitDisabled,
  onSubmit,
  onCancel
}) => ({
  onCloseMorphedContent
}) => {
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(View, {
      paddingInline: "500",
      paddingBlockEnd: "100",
      children: /*#__PURE__*/_jsx(LinkAdvancedPopoverControls, {
        relAttr: relAttr,
        onRelAttrChange: onRelAttrChange,
        disabled: linkType === LinkTypes.NONE
      })
    }), /*#__PURE__*/_jsx(HR, {
      distance: "xs"
    }), /*#__PURE__*/_jsx(Fieldset, {
      _size: "small",
      paddingInline: "500",
      paddingBlockEnd: "500",
      children: /*#__PURE__*/_jsx(LazyLoadingInput, {
        onChange: onLoadingValueChange,
        value: loadingValue,
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "RichTextLib.lazyLoading.imageLabel"
        })
      })
    }), /*#__PURE__*/_jsx(ExplicitSaveControls, {
      paddingBlockEnd: "500",
      paddingInline: "500",
      popoverType: PopoverType.EDIT,
      isSubmitDisabled: isSubmitDisabled,
      onSubmit: () => {
        const success = onSubmit();
        if (!success) {
          onCloseMorphedContent();
        }
      },
      onCancel: onCancel
    })]
  });
};
const renderImagePreview = ({
  altText,
  imageFileInfo,
  onReplace
}) => {
  const {
    name,
    size,
    src
  } = imageFileInfo;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(FormLabelWithHelpLink, {
      primaryText: i18nKey('imagePreviewLabel'),
      linkText: i18nKey('replace'),
      onLinkClick: onReplace
    }), /*#__PURE__*/_jsx(FileAttachment, {
      fileName: name,
      fileInfo: size ? I18n.formatSize(size) : undefined,
      renderPreview: () => /*#__PURE__*/_jsx(Image, {
        alt: altText,
        shape: "thumbnail",
        src: src
      })
    })]
  });
};
const propTypes = Object.assign({}, LinkPopoverControlsV2.propTypes, {
  relAttr: PropTypes.array,
  altText: PropTypes.string,
  imageFileInfo: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  onReplace: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
});
function ImagePopoverBodyEdit(props) {
  const {
      relAttr,
      onLinkTypeChange,
      altText: _altText,
      loadingValue: _loadingValue,
      onUpdate,
      onSave,
      onClose,
      imageFileInfo,
      onReplace,
      showRefAndTargetControls
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const [altText, setAltText] = useState(_altText);
  const [loadingValue, setLoadingValue] = useState(_loadingValue);
  const [handleLinkTypeChange, handleUpdate, _handleSubmit] = useLinkValidation({
    linkType: props.linkType,
    href: props.href,
    isValidLink: props.isValidLink,
    onLinkTypeChange,
    onUpdate,
    onSubmit: onSave
  });
  const handleCloseOnEscape = evt => {
    if (evt.key === KeyTypes.ESCAPE) {
      onClose();
    }
  };
  const handleSubmit = () => {
    return _handleSubmit({
      altText,
      loadingValue: LazyLoadingTypes[loadingValue]
    });
  };
  const handleRelAttrChange = value => {
    handleUpdate({
      relAttr: value
    });
  };
  const handleAltTextChange = evt => {
    const {
      value
    } = evt.target;
    setAltText(value);
  };
  const handleLoadingValueChange = evt => {
    const {
      value
    } = evt.target;
    setLoadingValue(value);
  };
  const handleSubmitOnEnter = evt => {
    if (evt.key === KeyTypes.ENTER) {
      handleSubmit();
      evt.preventDefault();
    }
  };
  const isSubmitDisabled = props.linkType !== LinkTypes.NONE && isWhitespace(props.href);
  let morphingPopoverProps = {};
  if (showRefAndTargetControls) {
    // Only show the advanced link controls for non-email contexts
    morphingPopoverProps = {
      morphButtonTitle: linkI18nKey('title'),
      morphedContentTitle: linkI18nKey('title'),
      renderMorphedContent: getRenderImageAdvancedControls({
        relAttr,
        onRelAttrChange: handleRelAttrChange,
        linkType: props.linkType,
        loadingValue,
        onLoadingValueChange: handleLoadingValueChange,
        tooltipClassName: props.tooltipClassName,
        isSubmitDisabled,
        onSubmit: handleSubmit,
        onCancel: onClose
      })
    };
  }
  return /*#__PURE__*/_jsx(View, {
    "data-test-id": "image-popover-body-edit",
    inlineSize: "294px",
    tabIndex: "-1",
    children: /*#__PURE__*/_jsx(MorphingPopoverBody, Object.assign({
      title: i18nKey('popoverTitle'),
      paddingInline: "500",
      paddingBlockStart: "500"
    }, morphingPopoverProps, {
      children: /*#__PURE__*/_jsxs("div", {
        onKeyDown: handleCloseOnEscape,
        children: [/*#__PURE__*/_jsx(View, {
          paddingInline: "500",
          paddingBlockEnd: "100",
          children: /*#__PURE__*/_jsx(LinkPopoverControlsV2, Object.assign({}, rest, {
            onLinkTypeChange: handleLinkTypeChange,
            onUpdate: handleUpdate,
            onSubmit: handleSubmit,
            onClose: onClose,
            showRefAndTargetControls: showRefAndTargetControls
          }))
        }), /*#__PURE__*/_jsx(HR, {
          distance: "xs",
          marginBlockStart: showRefAndTargetControls ? '200' : '400'
        }), /*#__PURE__*/_jsxs(Fieldset, {
          _size: "small",
          paddingInline: "500",
          paddingBlockEnd: "200",
          children: [renderImagePreview({
            altText,
            imageFileInfo,
            onReplace
          }), /*#__PURE__*/_jsx(FormControl, {
            label: /*#__PURE__*/_jsx(FormattedMessage, {
              message: i18nKey('altTextLabel')
            }),
            tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
              message: i18nKey('altTextHelp')
            }),
            tooltipPlacement: "top right",
            tooltipProps: {
              innerClassName: props.tooltipClassName
            },
            children: /*#__PURE__*/_jsx(TextInput, {
              autoFocus: true,
              autoFocusPreventScroll: true,
              name: "alt-text",
              value: altText,
              onChange: handleAltTextChange,
              onKeyDown: handleSubmitOnEnter
            })
          })]
        }), /*#__PURE__*/_jsx(ExplicitSaveControls, {
          paddingBlockStart: "300",
          paddingBlockEnd: "500",
          paddingInline: "500",
          popoverType: PopoverType.EDIT,
          isSubmitDisabled: isSubmitDisabled,
          onSubmit: handleSubmit,
          onCancel: onClose
        })]
      })
    }))
  });
}
ImagePopoverBodyEdit.propTypes = propTypes;
ImagePopoverBodyEdit.displayName = 'ImagePopoverBodyEdit';
export default ImagePopoverBodyEdit;